import { __assign } from "tslib";
import React from 'react';
import redraft from 'redraft';
import classNames from 'classnames';
import endsWith from 'lodash/endsWith';
import List from '../List';
import getPluginsViewer from '../PluginsViewer';
import { getTextDirection } from './textUtils';
import styleRenderer from '../styleRenderer';
// import { staticInlineStyleMapper } from '../staticInlineStyleMapper';
export var BLOCK_TYPES = [
    'header-one',
    'header-two',
    'header-three',
    'header-four',
    'header-five',
    'header-six',
    'unordered-list-item',
    'ordered-list-item',
    'blockquote',
    'atomic',
    'code-block',
    'unstyled',
];
var isEmptyContentState = function (raw) {
    return !raw || !raw.blocks || (raw.blocks.length === 1 && raw.blocks[0].text === '');
};
var isEmptyBlockOrHasAtomic = function (_a) {
    var _ = _a[0], data = _a[1];
    return (data && data.length === 0) ||
        // We can have more than one block here, we check the first one as that normally
        // means there is a block level plugin here
        (data && data.props && data.props.type && data.props.type.match(/plugin/));
};
var getBlockStyleClasses = function (data, mergedStyles, textDirection, classes) {
    var _a;
    var rtl = textDirection || data.textDirection;
    var defaultTextAlignment = rtl ? 'right' : 'left';
    var alignmentClass = data.textAlignment || defaultTextAlignment;
    return classNames(classes, (_a = {}, _a[mergedStyles.rtl] = rtl, _a), mergedStyles["textAlign-" + alignmentClass]);
};
var kebabToCamel = function (s) { return s.replace(/-([a-z])/, function (_, p1) { return p1.toUpperCase(); }); };
var kebabToCamelObjectKeys = function (obj) {
    if (obj === void 0) { obj = {}; }
    return Object.keys(obj).reduce(function (result, key) {
        result[kebabToCamel(key)] = obj[key];
        return result;
    }, {});
};
var blockDataToStyle = function (_a) {
    var dynamicStyles = _a.dynamicStyles;
    return kebabToCamelObjectKeys(dynamicStyles);
};
var getBlocks = function (mergedStyles, textDirection) {
    var getList = function (ordered) { return function (items, blockProps) {
        var fixedItems = items.map(function (item) { return (item.length ? item : [' ']); });
        var props = {
            key: blockProps.keys[0],
            items: fixedItems,
            ordered: ordered,
            mergedStyles: mergedStyles,
            textDirection: textDirection,
            blockProps: blockProps,
            getBlockStyleClasses: getBlockStyleClasses,
            blockDataToStyle: blockDataToStyle,
        };
        return React.createElement(List, __assign({}, props));
    }; };
    /* Blockquotes need a specific structure to make sure quotes
       are inserted correctly at the beginning and end of the block */
    var blockQuoteChildFactory = function (children) { return (React.createElement("div", null, Array.isArray(children)
        ? children.map(function (child) {
            return typeof child === 'string' && child ? React.createElement("span", null, child) : child;
        })
        : children)); };
    var blockFactory = function (type, style) {
        return function (children, blockProps) {
            return children.map(function (child, i) {
                var Type = typeof type === 'string' ? type : type(child);
                return (React.createElement(Type, { className: getBlockStyleClasses(blockProps.data[i], mergedStyles, textDirection, mergedStyles[style]), style: blockDataToStyle(blockProps.data[i]), key: "block-" + blockProps.keys[i] }, child.map(function (c) {
                    return type === 'blockquote' && c ? blockQuoteChildFactory(c) : c;
                })));
            });
        };
    };
    return {
        unstyled: blockFactory(function (child) { return (isEmptyBlockOrHasAtomic(child) ? 'div' : 'p'); }, 'text'),
        blockquote: blockFactory('blockquote', 'quote'),
        'header-one': blockFactory('h1', 'headerOne'),
        'header-two': blockFactory('h2', 'headerTwo'),
        'header-three': blockFactory('h3', 'headerThree'),
        'code-block': blockFactory('pre', 'codeBlock'),
        'unordered-list-item': getList(false),
        'ordered-list-item': getList(true),
    };
};
var getEntities = function (typeMap, pluginProps, styles) {
    return getPluginsViewer(typeMap, pluginProps, styles);
};
var normalizeContentState = function (contentState) { return (__assign(__assign({}, contentState), { blocks: contentState.blocks.map(function (block) {
        if (block.type === 'atomic') {
            return block;
        }
        var data = __assign({}, block.data);
        var direction = getTextDirection(block.text);
        if (direction === 'rtl') {
            data.textDirection = direction;
        }
        var text = block.text;
        if (endsWith(text, '\n')) {
            text += '\n';
        }
        // hacky parent block color handling, this takes
        // the first color of the block and sets it as the
        // color for the block.
        if (block.inlineStyleRanges.length > 0) {
            var firstColor = block.inlineStyleRanges[0];
            if (firstColor.style &&
                firstColor.style.startsWith('TEXT_TOOLS_COLOR_')) {
                data.dynamicStyles = __assign({ color: firstColor.style.replace('TEXT_TOOLS_COLOR_', '') }, data.dynamicStyles);
            }
        }
        return __assign(__assign({}, block), { data: data,
            text: text });
    }) })); };
var combineMappers = function (mappers) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    if (!mappers ||
        !mappers.length ||
        mappers.some(function (resolver) { return typeof resolver !== 'function'; })) {
        console.warn(mappers + " is expected to be a function array"); // eslint-disable-line no-console
        return {};
    }
    return mappers.reduce(function (map, mapper) { return Object.assign(map, mapper.apply(void 0, args)); }, {});
};
var redraftOptions = {
    cleanup: {
        after: BLOCK_TYPES.filter(function (t) { return t.indexOf('header') === -1; }),
        split: true,
        except: [
            'unordered-list-item',
            'ordered-list-item',
            'unstyled',
            'header-one',
            'header-two',
            'header-three',
        ],
    },
};
/**
 * Styles renderer - render ALL inline styles
 */
var getStyles = function () {
    return function (children, styleArray, params) {
        return styleRenderer(children, styleArray, params);
    };
};
var convertToReact = function (contentState, mergedStyles, textDirection, typeMap, entityProps, decorators, options) {
    if (options === void 0) { options = {}; }
    if (isEmptyContentState(contentState)) {
        return null;
    }
    return redraft(normalizeContentState(contentState), {
        styles: getStyles(),
        blocks: getBlocks(mergedStyles, textDirection),
        entities: getEntities(combineMappers(typeMap), entityProps, mergedStyles),
        decorators: decorators,
    }, __assign(__assign({}, redraftOptions), options));
};
export { convertToReact };
//# sourceMappingURL=convertContentState.js.map